import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Mail, MapPin, Calendar, Tag, MessageSquare, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { fetchPartnerLeads, updatePartnerLead } from "@/lib/api";

const STATUS_COLUMNS = [
  { id: "new", labelDe: "Neu", color: "#1F2420" },
  { id: "contacted", labelDe: "Kontaktiert", color: "#6F7F61" },
  { id: "qualified", labelDe: "Qualifiziert", color: "#B8A46A" },
  { id: "onboarded", labelDe: "Onboarded", color: "#3B4231" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return iso.slice(0, 10);
  }
}

function LeadCard({ lead, onUpdate, isPending }) {
  const [expanded, setExpanded] = useState(false);
  const [notesDraft, setNotesDraft] = useState(lead.notes || "");
  const [editingNotes, setEditingNotes] = useState(false);

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === lead.status) return;
    onUpdate(lead.id, { status: newStatus });
  };

  const handleSaveNotes = () => {
    if (notesDraft.trim() === (lead.notes || "").trim()) {
      setEditingNotes(false);
      return;
    }
    onUpdate(lead.id, { notes: notesDraft });
    setEditingNotes(false);
  };

  return (
    <div
      className="border border-soft rounded-sm bg-bg hover:border-primary transition-colors"
      data-testid={`lead-card-${lead.id}`}
      data-lead-status={lead.status}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-medium text-primary truncate">
              {lead.restaurant_name}
            </div>
            <div className="text-[11px] text-muted truncate flex items-center gap-1 mt-0.5">
              <MapPin size={10} />
              {lead.city}
              {lead.role && (
                <>
                  <span>·</span>
                  <span>{lead.role}</span>
                </>
              )}
            </div>
          </div>
          <span className="text-[10px] text-muted whitespace-nowrap flex items-center gap-1">
            <Calendar size={10} />
            {formatDate(lead.created_at)}
          </span>
        </div>

        <div className="mt-2 flex items-center gap-2 text-[11px] text-primary">
          <Mail size={10} className="text-muted shrink-0" />
          <a
            href={`mailto:${lead.email}`}
            className="truncate hover:text-accent"
            data-testid={`lead-${lead.id}-email`}
          >
            {lead.email}
          </a>
        </div>
        {lead.website_or_instagram && (
          <div className="mt-1 flex items-center gap-2 text-[11px] text-primary">
            <Globe size={10} className="text-muted shrink-0" />
            <a
              href={normalizeUrl(lead.website_or_instagram)}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-accent inline-flex items-center gap-1"
            >
              {lead.website_or_instagram}
              <ExternalLink size={9} />
            </a>
          </div>
        )}

        {lead.dietary_standards?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {lead.dietary_standards.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 border border-soft rounded-sm text-muted"
              >
                {tag}
              </span>
            ))}
            {lead.dietary_standards.length > 3 && (
              <span className="text-[10px] text-muted">+{lead.dietary_standards.length - 3}</span>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <select
            value={lead.status}
            onChange={handleStatusChange}
            disabled={isPending}
            className="text-[11px] border border-soft bg-bg rounded-sm px-2 py-1 text-primary focus:outline-none focus:border-primary disabled:opacity-50"
            data-testid={`lead-${lead.id}-status-select`}
          >
            {STATUS_COLUMNS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.labelDe}
              </option>
            ))}
            <option value="rejected">Abgelehnt</option>
          </select>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-muted hover:text-primary inline-flex items-center gap-1"
            data-testid={`lead-${lead.id}-expand`}
          >
            <ChevronDown
              size={12}
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0)",
                transition: "transform 0.2s",
              }}
            />
            {expanded ? "Weniger" : "Mehr"}
          </button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-soft text-[11px] space-y-1.5" data-testid={`lead-${lead.id}-details`}>
            <DetailRow label="Kontakt" value={lead.contact_name} />
            <DetailRow label="Telefon" value={lead.phone || "—"} />
            <DetailRow label="Bevorzugter Kanal" value={lead.preferred_contact_method} />
            <DetailRow
              label="Beste Erreichbarkeit"
              value={Array.isArray(lead.best_reachability) ? lead.best_reachability.join(", ") : lead.best_reachability}
            />
            {lead.preferred_call_timeframe && (
              <DetailRow label="Anruf-Zeitfenster" value={lead.preferred_call_timeframe} />
            )}
            {lead.message && (
              <div>
                <div className="text-[10px] tracking-widest uppercase text-muted mb-1">
                  <MessageSquare size={10} className="inline mr-1" /> Nachricht
                </div>
                <div className="text-primary leading-snug">{lead.message}</div>
              </div>
            )}
            {(lead.utm_source || lead.qr_source || lead.campaign_source) && (
              <DetailRow
                label="Quelle"
                value={
                  <span className="flex items-center gap-1 text-muted">
                    <Tag size={10} />
                    {[lead.qr_source, lead.utm_source, lead.campaign_source].filter(Boolean).join(" / ")}
                  </span>
                }
              />
            )}

            {/* Notes editor */}
            <div className="mt-2">
              <div className="text-[10px] tracking-widest uppercase text-muted mb-1">Notiz</div>
              {editingNotes ? (
                <div>
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    className="w-full text-[11px] border border-soft rounded-sm p-2 text-primary focus:outline-none focus:border-primary bg-bg"
                    data-testid={`lead-${lead.id}-notes-input`}
                    placeholder="Notiz hinzufügen…"
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={handleSaveNotes}
                      disabled={isPending}
                      className="text-[10px] px-2 py-1 bg-primary text-bg rounded-sm hover:bg-accent disabled:opacity-50"
                      data-testid={`lead-${lead.id}-notes-save`}
                    >
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNotesDraft(lead.notes || "");
                        setEditingNotes(false);
                      }}
                      className="text-[10px] px-2 py-1 border border-soft rounded-sm text-muted hover:text-primary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingNotes(true)}
                  className="text-primary leading-snug min-h-[18px] cursor-pointer hover:text-accent"
                  data-testid={`lead-${lead.id}-notes-display`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditingNotes(true);
                  }}
                >
                  {lead.notes ? (
                    lead.notes
                  ) : (
                    <span className="text-muted italic">Klicken um Notiz hinzuzufügen…</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[10px] tracking-widest uppercase text-muted w-28 shrink-0">{label}</span>
      <span className="text-primary flex-1 min-w-0">{value}</span>
    </div>
  );
}

function normalizeUrl(input) {
  if (!input) return "#";
  const v = input.trim();
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (v.startsWith("@")) return `https://instagram.com/${v.slice(1)}`;
  return `https://${v}`;
}

export default function LeadPipelineKanban() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["partner-leads"],
    queryFn: () => fetchPartnerLeads(500),
    refetchInterval: 60_000,
    retry: false,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }) => updatePartnerLead(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["partner-leads"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || "Update failed";
      toast.error(`Fehler: ${detail}`);
    },
  });

  const leadsByStatus = useMemo(() => {
    const groups = { new: [], contacted: [], qualified: [], onboarded: [], rejected: [] };
    (data?.items || []).forEach((lead) => {
      const s = lead.status || "new";
      if (!groups[s]) groups[s] = [];
      groups[s].push(lead);
    });
    return groups;
  }, [data]);

  const handleUpdate = (id, patch) => {
    updateMutation.mutate({ id, patch });
    if (patch.status) {
      toast.success(`Status auf "${STATUS_COLUMNS.find((c) => c.id === patch.status)?.labelDe || patch.status}" gesetzt`);
    } else if (patch.notes !== undefined) {
      toast.success("Notiz gespeichert");
    }
  };

  if (isLoading) {
    return (
      <div className="card-standard" data-testid="lead-kanban-loading">
        <div className="body-md">Lade Partner-Leads…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-standard" data-testid="lead-kanban-error">
        <div className="body-md text-muted">Fehler beim Laden der Leads.</div>
      </div>
    );
  }

  const totalLeads = data?.count || 0;

  return (
    <div className="card-standard" data-testid="lead-kanban" style={{ padding: 24 }}>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="form-label" style={{ color: "var(--color-accent)" }}>
            Lead-Pipeline
          </div>
          <div className="body-md mt-1">
            <strong className="text-primary">{totalLeads}</strong> Partner-Anfragen — verschiebe via Status-Dropdown
          </div>
        </div>
      </div>

      {totalLeads === 0 ? (
        <div className="border border-soft rounded-sm p-8 text-center body-md text-muted" data-testid="lead-kanban-empty">
          Noch keine Partner-Leads. Sobald Restaurants das Partner-Formular ausfüllen, erscheinen sie hier.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATUS_COLUMNS.map((col) => {
            const items = leadsByStatus[col.id] || [];
            return (
              <div
                key={col.id}
                className="border border-soft rounded-sm bg-surface/30 p-2 min-h-[300px]"
                data-testid={`lead-column-${col.id}`}
              >
                <div className="flex items-center justify-between px-2 pt-1 pb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ background: col.color }}
                    />
                    <span className="text-[11px] tracking-widest uppercase font-medium text-primary">
                      {col.labelDe}
                    </span>
                  </div>
                  <span
                    className="text-[11px] tabular-nums text-muted"
                    data-testid={`lead-column-${col.id}-count`}
                  >
                    {items.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {items.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onUpdate={handleUpdate}
                      isPending={updateMutation.isPending && updateMutation.variables?.id === lead.id}
                    />
                  ))}
                  {items.length === 0 && (
                    <div className="text-[11px] text-muted text-center py-6 px-2">
                      Leer
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(leadsByStatus.rejected || []).length > 0 && (
        <div className="mt-6 pt-6 border-t border-soft" data-testid="lead-rejected-section">
          <div className="form-label mb-3" style={{ color: "var(--color-accent)" }}>
            Abgelehnt ({(leadsByStatus.rejected || []).length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {(leadsByStatus.rejected || []).map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onUpdate={handleUpdate}
                isPending={updateMutation.isPending && updateMutation.variables?.id === lead.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
